import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { REPORT } from '@/constants/testIds';
import { toast } from 'sonner';
import { Copy, ExternalLink, RefreshCw, Share2 } from 'lucide-react';

export const ReportShareCard = () => {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/report-cards/mine');
      setCard(data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copy = async () => {
    if (!card) return;
    try {
      await navigator.clipboard.writeText(card.share_url);
      toast.success('SHARE_URL_COPIED');
    } catch {
      toast.error('Copy failed');
    }
  };

  const regen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/report-cards/regenerate');
      setCard(data);
      toast.success('SHORTCODE_REGENERATED');
    } catch {
      toast.error('Regen failed');
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    if (!card) return;
    setBusy(true);
    try {
      await api.post('/report-cards/deactivate');
      setCard(null);
      toast.success('DEACTIVATED');
      // The endpoint auto-mints a new one on next fetch; reload to get it
      setTimeout(load, 250);
    } catch {
      toast.error('Deactivate failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={REPORT.shareCard} className="c-card col-span-1 lg:col-span-3">
      <div className="border-b border-[#3F3F46] pb-3 mb-5 flex items-center justify-between">
        <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500 flex items-center gap-2">
          <Share2 className="w-3 h-3 text-[#38BDF8]" strokeWidth={2.5} />
          // PUBLIC_REPORT_CARD · REDACTED_SNAPSHOT
        </div>
        {card && (
          <div className="text-[10px] tracking-widest uppercase text-gray-600">
            SHARE_COUNT :: <span data-testid={REPORT.shareCount} className="text-white">{card.share_count}</span>
          </div>
        )}
      </div>
      {loading ? (
        <div className="text-xs text-gray-500 tracking-widest uppercase">
          LOADING<span className="caelix-dots"></span>
        </div>
      ) : !card ? (
        <div className="text-xs text-gray-500">&gt; card unavailable · try refresh</div>
      ) : (
        <>
          <p className="text-xs text-gray-500 mb-4 leading-relaxed max-w-2xl">
            &gt; A public, share-safe view of your operator stats. Callsign only — no email
            or entity values. Every view increments your share counter.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end mb-4">
            <div>
              <div className="c-label">SHARE_URL</div>
              <code
                data-testid={REPORT.shareUrl}
                className="block bg-[#27272A] border border-[#3F3F46] px-3 py-2 text-xs text-gray-300 break-all"
              >
                {card.share_url}
              </code>
            </div>
            <div className="flex gap-2">
              <button
                data-testid={REPORT.copyBtn}
                onClick={copy}
                className="c-btn-primary text-xs inline-flex items-center gap-1"
              >
                <Copy className="w-3 h-3" strokeWidth={2.5} /> COPY
              </button>
              <a
                href={card.share_url}
                target="_blank"
                rel="noopener noreferrer"
                className="c-btn-secondary text-xs inline-flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" strokeWidth={2.5} /> OPEN
              </a>
            </div>
          </div>
          <div className="pt-3 border-t border-[#3F3F46] flex flex-wrap gap-3 items-center justify-between">
            <div className="text-[10px] tracking-widest uppercase text-gray-600">
              SHORTCODE :: <span className="text-white font-bold">{card.shortcode}</span>
            </div>
            <div className="flex gap-2">
              <button
                data-testid={REPORT.regenBtn}
                onClick={regen}
                disabled={busy}
                className="c-btn-secondary text-[10px] inline-flex items-center gap-1"
              >
                <RefreshCw className="w-3 h-3" strokeWidth={2.5} /> REGENERATE
              </button>
              <button
                data-testid={REPORT.deactivateBtn}
                onClick={deactivate}
                disabled={busy}
                className="c-btn-danger text-[10px]"
              >
                DEACTIVATE
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportShareCard;
